import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Team → Activity and Team → Leaderboard (BACKLOG C-14 phase 2, W4,
 * GLE-8395). Every route is behind the backend switches `user_roles_enabled`
 * and `user_roles_insights_enabled`: while either is off the route answers
 * 404 with error_code 1035, which the pages read as "not switched on".
 *
 * The server checks every request; the app only hides what a user can't use.
 * Shapes are the contract frozen in the C-14 phase-2 coordination note.
 */

export const MANAGE_ACTIVITY_KINDS = {
  ROLE_GRANTED: "role_granted",
  ROLE_REVOKED: "role_revoked",
  POD_CREATED: "pod_created",
  POD_ARCHIVED: "pod_archived",
  POD_MEMBER_ADDED: "pod_member_added",
  POD_MEMBER_REMOVED: "pod_member_removed",
  BLOCK_BOOKED: "block_booked",
  BLOCK_MOVED: "block_moved",
  BLOCK_CANCELLED: "block_cancelled",
  SCORE_ADJUSTED: "score_adjusted",
  AVAILABILITY_APPROVED: "availability_approved",
  AVAILABILITY_DENIED: "availability_denied",
  // W1's campaign block settings (one row per changed setting).
  CAMPAIGN_SETTING_CHANGED: "campaign_setting_changed",
  // W3's Team → Reps writes.
  REP_CAMPAIGN_APPROVED: "rep_campaign_approved",
  REP_CAMPAIGN_REMOVED: "rep_campaign_removed",
  REP_DEACTIVATED: "rep_deactivated",
  REP_REACTIVATED: "rep_reactivated",
  REP_MOVED_OUT: "rep_moved_out",
  REP_MOVED_IN: "rep_moved_in",
  REP_STRIKE_EXCUSED: "rep_strike_excused",
  REP_PLATFORM_BANNED: "rep_platform_banned",
  REP_PLATFORM_UNBANNED: "rep_platform_unbanned",
} as const;

export type ManageActivityKindI =
  (typeof MANAGE_ACTIVITY_KINDS)[keyof typeof MANAGE_ACTIVITY_KINDS];

export interface ManageRefI {
  id: string;
  name: string | null;
}

export interface ManageActivityActorI {
  /**
   * manager: an LGA or Admin in this app · customer: the workspace's admin in
   * the customer app · admin: Glencoco staff · system: a seeded grant ·
   * unknown: the account is gone.
   */
  type: "manager" | "customer" | "admin" | "system" | "unknown";
  user_id?: string;
  /** "Removed user" for a deleted account; absent when unknown. */
  name?: string;
  email?: string;
}

export interface ManageActivityDetailsI {
  role?: "admin" | "lga" | "pod_lead";
  note?: string;
  from_start?: string;
  to_start?: string;
  from_campaign?: ManageRefI;
  to_campaign?: ManageRefI;
  points?: number;
  reason?: string;
  /** The ET Monday, YYYY-MM-DD. */
  week_start?: string;
  reduced_blocks?: number;
  reason_category?: string;
  review_notes?: string;
  /** campaign_setting_changed: the setting; an absent value is the default. */
  field?: string;
  old_value?: number;
  new_value?: number;
  /**
   * rep_moved_*: the workspace on the other side of the move, only when the
   * viewer manages it too; otherwise absent and other_workspace_hidden is
   * true (C14-W4-D108), and the sentence says "another workspace".
   */
  other_workspace?: ManageRefI;
  other_workspace_hidden?: boolean;
}

export interface ManageActivityEventI {
  id: string;
  kind: ManageActivityKindI;
  occurred_at: string;
  workspace: ManageRefI | null;
  actor: ManageActivityActorI;
  subject: { user_id: string; name: string | null } | null;
  campaign: ManageRefI | null;
  pod: ManageRefI | null;
  details: ManageActivityDetailsI;
}

export interface ManageActivityPageI {
  events: Array<ManageActivityEventI>;
  /** Pass back as `before` for the next page; null at the end. */
  next_token: string | null;
}

export interface ManageActivityParamsI {
  kinds?: Array<ManageActivityKindI>;
  before?: string;
  limit?: number;
}

const activityParams = ({ kinds, before, limit }: ManageActivityParamsI) => ({
  kinds: kinds?.length ? kinds.join(",") : undefined,
  before,
  limit,
});

/**
 * A 404 (+ 1035) means "switched off": an answer the pages read, not an error
 * to log. A fresh object per request, because the meta interceptor writes
 * into `meta` (as manage.ts does).
 */
const switchProbe = (
  config: AxiosRequestConfig = {}
): AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI => ({
  ...config,
  meta: { expectedStatuses: [404] },
});

export const ManageInsightsAPIEndpoints = (api: AxiosInstance) => ({
  /** Admins only (C-12 C; was an Admin or the workspace's LGA). */
  listWorkspaceActivity: (workspaceId: string, params: ManageActivityParamsI) =>
    api.get<ManageActivityPageI>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/activity`,
      switchProbe({ params: activityParams(params) })
    ),

  /** Admins only: every workspace, plus Admin and LGA grants. */
  listAllActivity: (params: ManageActivityParamsI) =>
    api.get<ManageActivityPageI>(
      "/v1/manage/activity",
      switchProbe({ params: activityParams(params) })
    ),
});

/** Team → Leaderboard periods (ET); `custom` takes from/to (to exclusive). */
export const LEADERBOARD_PERIODS = {
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  THIS_WEEK: "this_week",
  LAST_WEEK: "last_week",
  LAST_30_DAYS: "last_30_days",
  CUSTOM: "custom",
} as const;

export type LeaderboardPeriodKeyI =
  (typeof LEADERBOARD_PERIODS)[keyof typeof LEADERBOARD_PERIODS];

/**
 * A rep's (or campaign's, or team's) numbers. Activity by the day it
 * happened; meetings by the ET day of the meeting, one per prospect and
 * campaign, credited to the booker (as /leaderboard). USD. GMV only for an
 * LGA or Admin.
 */
export interface TeamCountsI {
  dials: number;
  connects: number;
  conversations: number;
  icp: number;
  booked: number;
  showed: number;
  qualified: number;
  /** Includes disqualified. */
  unqualified: number;
  no_show: number;
  cancelled: number;
  upcoming: number;
  /** Past with no outcome yet, or a payout dispute (`disputed`). */
  pending: number;
  disputed: number;
  paid: number;
  payout: number;
  payout_settled: number;
  payout_disputed: number;
  gmv?: number;
  gmv_settled?: number;
}

export interface TeamCampaignCountsI extends TeamCountsI {
  campaign_id: string;
}

export interface TeamRepI {
  user_id: string;
  name: string;
  email: string;
  pod_id: string | null;
  score: {
    score: number;
    letter_grade: string;
    trend: string;
    week_start: string;
  } | null;
  blocks: { booked: number; completed: number; fumbles: number };
  by_campaign: Array<TeamCampaignCountsI>;
}

export interface TeamCampaignI {
  id: string;
  name: string | null;
  is_homefix: boolean;
  /** Per qualified meeting, every rep, all time (the "at risk" basis). */
  avg_qualified_payout: number | null;
  avg_qualified_gmv?: number | null;
}

export interface TeamMeetingI {
  user_id: string;
  campaign_id: string;
  booked_at: string;
  meeting_date: string | null;
  status: string;
  payout: number;
  gmv?: number;
  is_paid: boolean;
}

export interface TeamLeaderboardI {
  period: { key: LeaderboardPeriodKeyI; from: string; to: string };
  activity_updated_at: string | null;
  activity_backfill_complete: boolean;
  campaigns: Array<TeamCampaignI>;
  pods: Array<{ id: string; name: string; member_ids: Array<string> }>;
  reps: Array<TeamRepI>;
  meetings: Array<TeamMeetingI>;
}

export interface TeamLeaderboardParamsI {
  period: LeaderboardPeriodKeyI;
  from?: string;
  to?: string;
}

export interface LgaTotalsI {
  roster: number;
  booked: number;
  showed: number;
  qualified: number;
  no_show: number;
  cancelled: number;
  /** Admins only (C-12 C, C12-D201). */
  payout?: number;
  gmv: number;
  campaigns_with_qm: number;
  reps_with_qm: number;
}

export interface LgaWorkspaceRowI {
  workspace_id: string;
  name: string;
  this: LgaTotalsI;
  prev: LgaTotalsI;
}

/**
 * One LGA in an LGA's comparison (C-12 C, C12-D201): their workspaces'
 * totals summed, no workspace names or payout; only the viewer's own row
 * lists their workspaces.
 */
export interface LgaPeerI {
  user_id: string;
  name: string;
  is_self: boolean;
  this: LgaTotalsI;
  prev: LgaTotalsI;
  workspaces?: Array<LgaWorkspaceRowI>;
}

/** Who asks for the LGAs comparison: the answer's shape depends on it. */
export const LGA_LEADERBOARD_VIEWERS = {
  ADMIN: "admin",
  LGA: "lga",
} as const;

export type LgaLeaderboardViewerI =
  (typeof LGA_LEADERBOARD_VIEWERS)[keyof typeof LGA_LEADERBOARD_VIEWERS];

/** An Admin gets `workspaces`; an LGA `lgas` (and no workspaces). */
export interface LgaLeaderboardI {
  month: string;
  workspaces: Array<LgaWorkspaceRowI>;
  lgas?: Array<LgaPeerI>;
}

/** A cold build reads every /leaderboard card: give it the leaderboard's time. */
const TEAM_LEADERBOARD_TIMEOUT_MS = 60_000;

export const ManageLeaderboardAPIEndpoints = (api: AxiosInstance) => ({
  /** An Admin or the workspace's LGA; a Pod Lead gets their members only. */
  getTeamLeaderboard: (workspaceId: string, params: TeamLeaderboardParamsI) =>
    api.get<TeamLeaderboardI>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/leaderboard`,
      switchProbe({ params, timeout: TEAM_LEADERBOARD_TIMEOUT_MS })
    ),

  /**
   * Every calling workspace for a month and the one before (an Admin), or
   * one row per LGA (an LGA, since C-12 C). For an LGA a 401 is a backend
   * from before C-12 C, which the tab explains, so it isn't logged; an
   * Admin's 401 still is.
   */
  getLgaLeaderboard: (month?: string, { isLgaViewer = false } = {}) =>
    api.get<LgaLeaderboardI>("/v1/manage/lga_leaderboard", {
      params: { month },
      timeout: TEAM_LEADERBOARD_TIMEOUT_MS,
      meta: { expectedStatuses: isLgaViewer ? [404, 401] : [404] },
    } as AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI),
});
